import React, { useEffect, useMemo, useState } from "react"
import {
    Container,
    Row,
    Col,
    Table,
    Card,
    CardBody,
    Modal,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import NewPagination from "components/Common/Newpagination"
import { useGlobalFilter, usePagination, useTable } from "react-table"
import { useNavigate, useParams } from "react-router-dom"
import { fetchNodeData, FetchNodePutData } from "utils/common-fetchData"
import { formatDateTime } from "utils/formatDate"
import secureLocalStorage from "react-secure-storage"
import Loader from "pages/Separate/Loader"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

// answers is the enriched array from API:
// [{ question_id, type, user_answer, correct_answer, isCorrect, ai_graded, ai_explanation,
//    ai_score, reviewer_score, reviewer_override, max_score, question }]
// DB type: 1 = MCQ, 2 = Code Editor, 3 = Text Input
function calcScore(answers = []) {
    let earned = 0, maxPossible = 0
    let mcqCorrect = 0, mcqTotal = 0
    let tcEarned = 0, tcMax = 0, tcGraded = 0, tcTotal = 0, tcPending = 0

    for (const a of answers) {
        if (a.type === 1) {
            mcqTotal++
            maxPossible += 1
            if (a.isCorrect) { earned += 1; mcqCorrect++ }
        } else if (a.type === 2 || a.type === 3) {
            tcTotal++
            const ms = a.max_score || 10
            const effective = a.reviewer_score != null ? a.reviewer_score
                : (a.ai_score != null ? a.ai_score : null)
            if (effective !== null) {
                tcGraded++
                maxPossible += ms
                tcMax += ms
                earned += effective
                tcEarned += effective
            } else {
                tcPending++
            }
        }
    }

    const overallPct = maxPossible > 0 ? Math.round((earned / maxPossible) * 100) : 0
    const mcqPct = mcqTotal > 0 ? Math.round((mcqCorrect / mcqTotal) * 100) : 0

    return {
        mcqCorrect, mcqTotal, mcqPct,
        tcEarned, tcMax, tcGraded, tcTotal, tcPending,
        total: answers.length,
        overallPct,
        // backward compat aliases
        manualTotal: tcTotal, manualGraded: tcGraded, pendingManual: tcPending,
        pct: overallPct,
    }
}

function ScoreBadge({ pct }) {
    const color = pct >= 75 ? "#27ae60" : pct >= 50 ? "#f39c12" : "#e74c3c"
    const bg = pct >= 75 ? "#e8f5e9" : pct >= 50 ? "#fff8e1" : "#fdecea"
    return (
        <span style={{
            background: bg,
            color,
            border: `1.5px solid ${color}`,
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 13,
            fontWeight: 700,
        }}>
            {pct}%
        </span>
    )
}

const VIO_META = {
    tab_switch: { icon: "fas fa-exchange-alt", color: "#e67e22", bg: "#fff8e1", border: "#ffc107" },
    window_blur: { icon: "fas fa-desktop", color: "#e74c3c", bg: "#fdecea", border: "#ef9a9a" },
    fullscreen_exit: { icon: "fas fa-compress-alt", color: "#8e44ad", bg: "#f3e5f5", border: "#ce93d8" },
}

function ViolationsModal({ isOpen, onClose, violations = [], candidateName }) {
    const leaveCount = violations.filter(v => v.kind === "leave" || !v.kind).length

    return (
        <Modal isOpen={isOpen} size="md" centered toggle={onClose}>
            <div className="modal-header px-4">
                <h5 className="modal-title mt-0">
                    <i className="fas fa-shield-alt me-2" style={{ color: "#e74c3c" }}></i>
                    Proctoring Activity
                    {candidateName && <span className="ms-2 fw-normal" style={{ fontSize: 14, color: "#666" }}>— {candidateName}</span>}
                </h5>
                <button type="button" onClick={onClose} className="btn-modal-close">
                    <span aria-hidden="true" className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body px-4 py-3">
                {violations.length === 0 ? (
                    <div className="text-center py-4">
                        <i className="fas fa-check-circle" style={{ fontSize: 36, color: "#27ae60" }}></i>
                        <p className="mt-2 mb-0" style={{ color: "#555" }}>No violations recorded for this session.</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge" style={{ background: "#e74c3c", fontSize: 12 }}>
                                {leaveCount} violation{leaveCount !== 1 ? "s" : ""}
                            </span>
                            <span style={{ fontSize: 11, color: "#aaa" }}>
                                <i className="fas fa-info-circle me-1"></i>
                                App name detection is not available in browsers
                            </span>
                        </div>

                        {/* Timeline */}
                        <div style={{ position: "relative", paddingLeft: 24 }}>
                            {/* vertical line */}
                            <div style={{ position: "absolute", left: 8, top: 8, bottom: 8, width: 2, background: "#e9ecef", borderRadius: 2 }} />

                            {violations.map((v, i) => {
                                const isResume = v.kind === "resume" || v.type === "exam_resumed"
                                const meta = isResume
                                    ? { icon: "fas fa-play-circle", color: "#27ae60", bg: "#e8f5e9", border: "#a5d6a7" }
                                    : VIO_META[v.type] || { icon: "fas fa-exclamation-triangle", color: "#555", bg: "#f5f5f5", border: "#ccc" }

                                return (
                                    <div key={i} style={{ position: "relative", marginBottom: 10 }}>
                                        {/* dot on timeline */}
                                        <div style={{
                                            position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)",
                                            width: 12, height: 12, borderRadius: "50%",
                                            background: meta.color, border: "2px solid #fff",
                                            boxShadow: `0 0 0 2px ${meta.color}`,
                                        }} />
                                        <div className="d-flex align-items-center gap-2 rounded px-3 py-2"
                                            style={{ background: meta.bg, border: `1.5px solid ${meta.border}` }}>
                                            <i className={meta.icon} style={{ color: meta.color, fontSize: 14, minWidth: 16 }}></i>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: meta.color }}>
                                                    {v.label}
                                                    {isResume && v.durationAway != null && (
                                                        <span style={{ fontSize: 11, fontWeight: 400, color: "#666", marginLeft: 6 }}>
                                                            (away {v.durationAway}s)
                                                        </span>
                                                    )}
                                                </div>
                                                {!isResume && (
                                                    <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
                                                        {v.type === "tab_switch" && "Switched to another browser tab"}
                                                        {v.type === "window_blur" && "Switched to another application or OS window"}
                                                        {v.type === "fullscreen_exit" && "Exited fullscreen mode"}
                                                    </div>
                                                )}
                                                {v.question_index != null && (
                                                    <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                                                        <i className="fas fa-question-circle me-1" style={{ color: "#752a97" }}></i>
                                                        On Question {v.question_index}
                                                    </div>
                                                )}
                                            </div>
                                            <span style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap" }}>
                                                <i className="fas fa-clock me-1"></i>{v.time}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}

function DetailModal({ isOpen, onClose, response }) {
    const [localAnswers, setLocalAnswers] = useState([])
    const [overrideLoading, setOverrideLoading] = useState({})
    const [scoreInputs, setScoreInputs] = useState({})

    useEffect(() => {
        const answers = response?.answers || []
        setLocalAnswers(answers)
        setOverrideLoading({})
        const inputs = {}
        for (const a of answers) {
            if (a.type === 2 || a.type === 3) {
                const effective = a.reviewer_score != null ? a.reviewer_score
                    : (a.ai_score != null ? a.ai_score : 0)
                inputs[a.question_id] = String(effective)
            }
        }
        setScoreInputs(inputs)
    }, [response?.id])

    if (!response) return null

    const score = calcScore(localAnswers)
    const violationCount = (response.violations || []).length
    const overallColor = score.overallPct >= 75 ? "#27ae60" : score.overallPct >= 50 ? "#f39c12" : "#e74c3c"

    const handleOverride = async (ans, score) => {
        const qid = ans.question_id
        const reviewerId = localStorage.getItem("auth_id") || "0"
        setOverrideLoading(prev => ({ ...prev, [qid]: true }))
        try {
            await FetchNodePutData(
                () => {},
                `preassesment/response/grade/${response.id}/${qid}`,
                reviewerId,
                { score }
            )
            setLocalAnswers(prev =>
                prev.map(a => a.question_id === qid
                    ? { ...a, reviewer_score: score, reviewer_override: true, reviewer_at: new Date().toISOString() }
                    : a
                )
            )
        } catch (e) {
            console.error("Grade override failed", e)
        }
        setOverrideLoading(prev => ({ ...prev, [qid]: false }))
    }

    return (
        <Modal isOpen={isOpen} size="lg" centered toggle={onClose}>
            <div className="modal-header px-4">
                <h5 className="modal-title mt-0">
                    Response Detail — <b>{response.name}</b>
                </h5>
                <button type="button" onClick={onClose} className="btn-modal-close">
                    <span aria-hidden="true" className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body px-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>

                {/* Compact summary strip */}
                <div className="d-flex align-items-center gap-4 px-3 py-2 mb-4 rounded flex-wrap"
                    style={{ background: "#f8f4fe", border: "1px solid #e9d5ff" }}>
                    <div style={{ textAlign: "center", minWidth: 48 }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: overallColor, lineHeight: 1.1 }}>
                            {score.overallPct}%
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>Overall</div>
                    </div>

                    <div style={{ width: 1, height: 36, background: "#ddd", flexShrink: 0 }} />

                    {score.mcqTotal > 0 && (
                        <>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: "#27ae60" }}>
                                    {score.mcqCorrect}
                                    <span style={{ fontSize: 11, fontWeight: 500, color: "#aaa" }}>/{score.mcqTotal}</span>
                                </div>
                                <div style={{ fontSize: 10, color: "#888" }}>MCQ Correct</div>
                            </div>
                            {score.manualTotal > 0 && <div style={{ width: 1, height: 36, background: "#ddd", flexShrink: 0 }} />}
                        </>
                    )}

                    {score.tcTotal > 0 && (
                        <>
                            <div style={{ textAlign: "center" }}>
                                {score.tcPending > 0 ? (
                                    <>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: "#f39c12" }}>
                                            {score.tcPending}
                                            <span style={{ fontSize: 11, fontWeight: 400, color: "#aaa", marginLeft: 2 }}>pending</span>
                                        </div>
                                        <div style={{ fontSize: 10, color: "#888" }}>Text/Code</div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: "#0d6efd" }}>
                                            {score.tcEarned}
                                            <span style={{ fontSize: 11, fontWeight: 500, color: "#aaa" }}>/{score.tcMax}pts</span>
                                        </div>
                                        <div style={{ fontSize: 10, color: "#888" }}>Text/Code</div>
                                    </>
                                )}
                            </div>
                            <div style={{ width: 1, height: 36, background: "#ddd", flexShrink: 0 }} />
                        </>
                    )}

                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#752a97" }}>{score.total}</div>
                        <div style={{ fontSize: 10, color: "#888" }}>Questions</div>
                    </div>

                    <div style={{ width: 1, height: 36, background: "#ddd", flexShrink: 0 }} />

                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: violationCount > 0 ? "#e74c3c" : "#27ae60" }}>
                            {violationCount}
                        </div>
                        <div style={{ fontSize: 10, color: "#888" }}>Violations</div>
                    </div>
                </div>

                <div style={{ borderTop: "1px solid #eee", marginBottom: 16, paddingTop: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#333" }}>
                        <i className="fas fa-list me-2" style={{ color: "#752a97" }}></i>Question Responses
                    </span>
                </div>

                {/* Question-by-question */}
                {localAnswers.map((ans, i) => {
                    const isMcq = ans.type === 1
                    const isCode = ans.type === 3
                    const isCorrect = ans.isCorrect
                    const qText = ans.question?.question || `Question ${ans.question_id}`
                    const isOverriding = !!overrideLoading[ans.question_id]
                    const maxS = ans.max_score || 10
                    const effectiveScore = !isMcq
                        ? (ans.reviewer_score != null ? ans.reviewer_score : (ans.ai_score != null ? ans.ai_score : null))
                        : null
                    const tcRatio = effectiveScore != null ? effectiveScore / maxS : null

                    let optionMap = {}
                    if (isMcq && ans.question?.options) {
                        try { optionMap = JSON.parse(ans.question.options) } catch { }
                    }
                    const userOptionText = isMcq
                        ? (optionMap[ans.user_answer]
                            ? `Option ${ans.user_answer} — ${optionMap[ans.user_answer]}`
                            : ans.user_answer || "No answer")
                        : null
                    const correctOptionText = isMcq && ans.correct_answer
                        ? `Option ${ans.correct_answer} — ${optionMap[ans.correct_answer] ?? ans.correct_answer}`
                        : null

                    const borderColor = isMcq
                        ? (isCorrect === true ? "#27ae60" : isCorrect === false ? "#e74c3c" : "#752a97")
                        : (tcRatio !== null
                            ? (tcRatio >= 0.75 ? "#27ae60" : tcRatio >= 0.5 ? "#f39c12" : "#e74c3c")
                            : "#752a97")

                    return (
                        <div key={ans.question_id ?? i} className="mb-3 border rounded p-3"
                            style={{ borderLeft: `4px solid ${borderColor}` }}>
                            <div className="d-flex align-items-start gap-2 mb-2 flex-wrap">
                                <span className="badge" style={{ background: "#752a97", fontSize: 12 }}>Q{i + 1}</span>
                                <span className="badge" style={{ background: isMcq ? "#0d6efd" : isCode ? "#6f42c1" : "#6c757d", fontSize: 11 }}>
                                    {isMcq ? "MCQ" : isCode ? "Code" : "Text"}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#333", flex: 1 }}>{qText}</span>
                                <div className="d-flex align-items-center gap-1 ms-auto flex-wrap">
                                    {isMcq ? (
                                        <>
                                            {isCorrect === true && <span className="badge bg-success">Correct</span>}
                                            {isCorrect === false && <span className="badge bg-danger">Wrong</span>}
                                            {isCorrect === null && <span className="badge bg-secondary">Manual Review</span>}
                                        </>
                                    ) : (
                                        <>
                                            {ans.ai_graded && ans.ai_score != null && (
                                                <span style={{ fontSize: 10, fontWeight: 600, color: "#752a97", background: "#f5eeff", border: "1px solid #d8b4fe", borderRadius: 20, padding: "2px 8px" }}>
                                                    <i className="fas fa-robot me-1"></i>AI: {ans.ai_score}/{maxS}
                                                </span>
                                            )}
                                            {!ans.ai_graded && ans.ai_score == null && (
                                                <span className="badge bg-secondary">Not Graded</span>
                                            )}
                                            {ans.reviewer_override && (
                                                <span style={{ fontSize: 10, fontWeight: 600, color: "#0369a1", background: "#e0f2fe", border: "1px solid #7dd3fc", borderRadius: 20, padding: "2px 8px" }}>
                                                    <i className="fas fa-user-edit me-1"></i>Reviewed: {ans.reviewer_score}/{maxS}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* User's answer */}
                            {isMcq ? (
                                <div className="mb-1" style={{ fontSize: 13 }}>
                                    <span style={{ color: "#666" }}>Selected: </span>
                                    <b style={{ color: isCorrect === true ? "#27ae60" : isCorrect === false ? "#e74c3c" : "#333" }}>
                                        {userOptionText}
                                    </b>
                                </div>
                            ) : (
                                ans.user_answer ? (
                                    <pre style={{ background: isCode ? "#1e1e2e" : "#f8f8f8", color: isCode ? "#cdd6f4" : "#333", borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: isCode ? "monospace" : "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                                        {ans.user_answer}
                                    </pre>
                                ) : (
                                    <span style={{ color: "#aaa", fontSize: 13, fontStyle: "italic" }}>No answer provided</span>
                                )
                            )}

                            {/* Correct answer (MCQ only) */}
                            {correctOptionText && (
                                <div style={{ marginTop: 6, fontSize: 12, color: "#27ae60" }}>
                                    <i className="fas fa-check-circle me-1"></i>
                                    Correct: <b>{correctOptionText}</b>
                                </div>
                            )}

                            {/* AI explanation + score override for Text / Code */}
                            {!isMcq && ans.user_answer && (
                                <div className="mt-2">
                                    {ans.ai_explanation && (
                                        <div className="mb-2 px-2 py-2 rounded d-flex align-items-start gap-2"
                                            style={{ background: "#f5f0ff", border: "1px solid #d8b4fe", fontSize: 12 }}>
                                            <i className="fas fa-robot mt-1" style={{ color: "#752a97", fontSize: 13, minWidth: 14 }}></i>
                                            <span style={{ color: "#444", lineHeight: 1.5 }}>{ans.ai_explanation}</span>
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span style={{ fontSize: 11, color: "#888" }}>
                                            Score <span style={{ fontSize: 10, color: "#aaa" }}>(0–{maxS})</span>:
                                        </span>
                                        <input
                                            type="number"
                                            min={0}
                                            max={maxS}
                                            value={scoreInputs[ans.question_id] ?? ""}
                                            onChange={e => setScoreInputs(prev => ({ ...prev, [ans.question_id]: e.target.value }))}
                                            style={{ width: 70, fontSize: 13, padding: "2px 6px", borderRadius: 4, border: "1.5px solid #d0c4e8" }}
                                        />
                                        <button
                                            className="btn btn-sm btn-purple"
                                            style={{ fontSize: 12, padding: "2px 12px" }}
                                            disabled={isOverriding}
                                            onClick={() => handleOverride(ans, Math.min(maxS, Math.max(0, parseInt(scoreInputs[ans.question_id] ?? "0") || 0)))}
                                        >
                                            {isOverriding ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-save me-1"></i>Save</>}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Modal>
    )
}

function TableContainer({ columns, data }) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        state,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        gotoPage,
        setPageSize,
        pageOptions,
        setGlobalFilter,
        prepareRow,
    } = useTable({ columns, data }, useGlobalFilter, usePagination)

    const { pageIndex, pageSize, globalFilter } = state
    const navigate = useNavigate()

    return (
        <div className="px-2">
            <Row className="mb-2">
                <Col lg={2}>
                    <select
                        className="border-radius form-select"
                        style={{ width: "100px" }}
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 30, 40, 50].map(size => (
                            <option key={size} value={size}>Show {size}</option>
                        ))}
                    </select>
                </Col>
                <Col className="d-flex justify-content-end">
                    <div className="me-4">
                        <div className="search-box d-inline-block">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="border-radius form-control"
                                    placeholder="Search..."
                                    value={globalFilter || ""}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                />
                                <i className="bx bx-search-alt search-icon"></i>
                            </div>
                        </div>
                    </div>
                    <button
                        className="border-radius btn text-white ms-md-2 btn-red"
                        onClick={() => navigate(-1)}
                    >
                        <i className="dripicons-cross me-2 icon-center"></i>Back
                    </button>
                </Col>
            </Row>

            <Table className="table table-bordered mt-4" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup, i) => (
                        <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <th key={index} className={column.className} {...column.getHeaderProps()}>
                                    {column.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr key={i} {...row.getRowProps()}>
                                {row.cells.map((cell, index) => (
                                    <td key={index} className={cell.column.className} {...cell.getCellProps()}>
                                        {cell.render("Cell")}
                                    </td>
                                ))}
                            </tr>
                        )
                    })}
                </tbody>
            </Table>

            <Row className="justify-content-md-end justify-content-center align-items-center">
                <NewPagination
                    gotoPage={gotoPage}
                    canPreviousPage={canPreviousPage}
                    previousPage={previousPage}
                    canNextPage={canNextPage}
                    pageIndex={pageIndex}
                    pageOptions={pageOptions}
                    pageSize={pageSize}
                    page={page}
                    data={data}
                    nextPage={nextPage}
                />
            </Row>
        </div>
    )
}

const TYPE_LABEL = { 1: "MCQ", 2: "Text Input", 3: "Code Editor" }

function exportResponse(response) {
    const score = calcScore(response.answers || [])
    const violations = response.violations || []
    const leaveCount = violations.filter(v => v.kind === "leave" || !v.kind).length
    const answers = response.answers || []
    const answered = answers.filter(a => a.user_answer && String(a.user_answer).trim()).length

    const wb = XLSX.utils.book_new()

    /* ── Sheet 1: Summary ── */
    const summaryRows = [
        ["Field", "Value"],
        ["Candidate Name", response.name || ""],
        ["Email", response.email || ""],
        ["Date", formatDateTime(response.created_at) || ""],
        ["", ""],
        ["Total Questions", score.total],
        ["Answered", `${answered} / ${score.total}`],
        ["", ""],
        ["MCQ Questions", score.mcqTotal],
        ["Correct (MCQ)", score.mcqCorrect],
        ["Wrong (MCQ)", score.mcqTotal - score.mcqCorrect],
        ["MCQ Score", `${score.mcqPct}%`],
        ["", ""],
        ["Text/Code Questions", score.tcTotal],
        ["Text/Code Graded", score.tcGraded],
        ["T/C Earned Score", score.tcEarned],
        ["T/C Max Score", score.tcMax],
        ["Pending Review", score.tcPending],
        ["", ""],
        ["Overall Score", `${score.overallPct}%`],
        ["", ""],
        ["Violations", leaveCount],
    ]
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows)
    wsSummary["!cols"] = [{ wch: 22 }, { wch: 36 }]
    // Bold the header row
    wsSummary["A1"] = { v: "Field", t: "s", s: { font: { bold: true } } }
    wsSummary["B1"] = { v: "Value", t: "s", s: { font: { bold: true } } }
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary")

    /* ── Sheet 2: Question Responses ── */
    const qRows = answers.map((ans, i) => {
        const isMcq = ans.type === 1
        let optionMap = {}
        if (isMcq && ans.question?.options) {
            try { optionMap = JSON.parse(ans.question.options) } catch { }
        }
        const userAns = isMcq
            ? (optionMap[ans.user_answer]
                ? `Option ${ans.user_answer} — ${optionMap[ans.user_answer]}`
                : ans.user_answer || "No answer")
            : (ans.user_answer || "No answer")

        const correctAns = isMcq && ans.correct_answer
            ? `Option ${ans.correct_answer} — ${optionMap[ans.correct_answer] ?? ans.correct_answer}`
            : (isMcq ? ans.correct_answer || "" : ans.correct_answer || "")

        let result
        if (isMcq) {
            result = ans.isCorrect === true ? "Correct" : ans.isCorrect === false ? "Wrong" : "Not Graded"
        } else {
            const effective = ans.reviewer_score != null ? ans.reviewer_score
                : (ans.ai_score != null ? ans.ai_score : null)
            if (effective === null) {
                result = "Not Graded"
            } else {
                const ms = ans.max_score || 10
                result = `${effective}/${ms}${ans.reviewer_override ? " (Reviewed)" : ans.ai_graded ? " (AI)" : ""}`
            }
        }

        return {
            "#": i + 1,
            "Question": ans.question?.question || `Question ${ans.question_id}`,
            "Type": TYPE_LABEL[Number(ans.type)] || "Unknown",
            "User Answer": userAns,
            "Correct Answer": correctAns,
            "Result": result,
        }
    })

    const wsAnswers = XLSX.utils.json_to_sheet(qRows)
    wsAnswers["!cols"] = [
        { wch: 4 },   // #
        { wch: 60 },  // Question
        { wch: 14 },  // Type
        { wch: 40 },  // User Answer
        { wch: 40 },  // Correct Answer
        { wch: 14 },  // Result
    ]
    XLSX.utils.book_append_sheet(wb, wsAnswers, "Responses")

    /* ── Sheet 3: Violations ── */
    if (violations.length > 0) {
        const vRows = violations.map((v, i) => ({
            "#": i + 1,
            "Type": v.type || "",
            "Label": v.label || "",
            "Time": v.time || "",
            "Kind": v.kind || "leave",
        }))
        const wsVio = XLSX.utils.json_to_sheet(vRows)
        wsVio["!cols"] = [{ wch: 4 }, { wch: 20 }, { wch: 36 }, { wch: 14 }, { wch: 10 }]
        XLSX.utils.book_append_sheet(wb, wsVio, "Violations")
    }

    const safeName = (response.name || "Candidate").replace(/[^a-z0-9_ \-]/gi, "").trim()
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${safeName}_Assessment_Response.xlsx`)
}

const AssessmentResponsesIndex = () => {
    const { testId } = useParams()
    const token = secureLocalStorage.getItem("auth_token")
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [detailModal, setDetailModal] = useState(false)
    const [selectedResponse, setSelectedResponse] = useState(null)
    const [violationsModal, setViolationsModal] = useState(false)
    const [selectedViolations, setSelectedViolations] = useState({ list: [], name: "" })

    useEffect(() => {
        if (!testId) { setLoading(false); return }
        fetchNodeData(setLoading, `preassesment/data/getResponse/${testId}`, token)
            .then(res => {
                if (res?.success) setData(res.data || [])
            })
    }, [testId])

    const openDetail = row => {
        setSelectedResponse(row)
        setDetailModal(true)
    }

    const openViolations = row => {
        setSelectedViolations({ list: row.violations || [], name: row.name })
        setViolationsModal(true)
    }

    const columns = useMemo(
        () => [
            {
                Header: "#",
                accessor: "id",
                className: "text-center",
            },
            {
                Header: "Candidate",
                accessor: "name",
                className: "",
            },
            {
                Header: "Email",
                accessor: "email",
                className: "",
            },
            {
                Header: "Date",
                accessor: "created_at",
                className: "text-center",
                Cell: ({ value }) => formatDateTime(value) || "—",
            },
            {
                Header: "Score",
                accessor: "score",
                className: "text-center",
                Cell: ({ row }) => {
                    const sc = calcScore(row.original.answers || [])
                    return (
                        <div className="text-center">
                            <ScoreBadge pct={sc.overallPct} />
                        </div>
                    )
                },
            },
            {
                Header: "Answered",
                accessor: "answered",
                className: "text-center",
                Cell: ({ row }) => {
                    const answers = row.original.answers || []
                    const answered = answers.filter(a => a.user_answer && a.user_answer.toString().trim()).length
                    return (
                        <span style={{ fontWeight: 600 }}>
                            {answered} / {answers.length}
                        </span>
                    )
                },
            },
            {
                Header: "Violations",
                accessor: "violation_count",
                className: "text-center",
                Cell: ({ row }) => {

                    const count = row.original.violations.filter(v => v.kind === "leave").length || 0
                    if (count === 0)
                        return (
                            <span style={{ background: "#e8f5e9", color: "#2e7d32", border: "1.5px solid #a5d6a7", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                                <i className="fas fa-check me-1"></i>Clean
                            </span>
                        )
                    return (
                        <span
                            onClick={() => openViolations(row.original)}
                            style={{ background: "#fdecea", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                            title="Click to view violations"
                        >
                            <i className="fas fa-exclamation-triangle me-1"></i>{count}
                        </span>
                    )
                },
            },
            {
                Header: "Action",
                accessor: "action",
                className: "text-center",
                Cell: ({ row }) => (
                    <div className="d-flex gap-1 justify-content-center">
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openDetail(row.original)}
                            title="View result"
                        >
                            <i className="fas fa-eye me-1"></i>Result
                        </button>
                        <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => exportResponse(row.original)}
                            title="Export to Excel"
                        >
                            <i className="fas fa-file-excel me-1"></i>Export
                        </button>
                    </div>
                ),
            },
        ],
        [openViolations]
    )

    if (loading) return <Loader />

    return (
        <React.Fragment>
            <div className="page-content mb-4">
                <Container fluid>
                    <div className="px-2">
                        <Breadcrumbs title="Assessment" breadcrumbItem="Responses" />
                    </div>
                    <Card>
                        <CardBody>
                            <div className="responsive-table">
                                <TableContainer columns={columns} data={data} />
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <DetailModal
                isOpen={detailModal}
                onClose={() => setDetailModal(false)}
                response={selectedResponse}
            />
            <ViolationsModal
                isOpen={violationsModal}
                onClose={() => setViolationsModal(false)}
                violations={selectedViolations.list}
                candidateName={selectedViolations.name}
            />
        </React.Fragment>
    )
}

export default AssessmentResponsesIndex
